import React, { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MessageCircle, Timer, MapPin, Phone, IndianRupee, CheckCircle2, XCircle, Loader2, Navigation, Volume2, VolumeX, Clock, Star, Briefcase, Upload, Bell, Activity, UserCheck, AlertTriangle, CreditCard, Calendar, Camera, Settings, Save } from "lucide-react";
import BookingDetailsDialog from "../components/BookingDetailsDialog";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, API, WS_URL } from "../lib/api";
import Navbar from "../components/Navbar";
import Seo from "../components/Seo";
import MapView from "../components/MapView";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { toast } from "sonner";
import useAutoRefresh from "../lib/useAutoRefresh";
import { registerPush, unregisterPush } from "../lib/pushService";
import { useBookingCounts } from "../lib/BookingCountContext";

// Looping ringtone — tries immediately, retries fast until it plays
function createRinger() {
  let playing = false;
  let timer = null;
  let audio = null;

  function tryPlay() {
    if (!playing) return;
    if (audio) { try { audio.pause(); audio.src = ""; } catch {} }
    const a = new Audio("/notification.mp3");
    a.volume = 0.5;
    a.loop = true;
    a.currentTime = 0;
    a.play().then(() => {
      audio = a;
      if (timer) { clearInterval(timer); timer = null; }
    }).catch(() => {
      if (playing && !timer) timer = setInterval(tryPlay, 200);
    });
  }

  return {
    start() {
      if (playing) return;
      playing = true;
      if (audio) { try { audio.pause(); audio.src = ""; } catch {} audio = null; }
      if (timer) { clearInterval(timer); timer = null; }
      tryPlay();
    },
    stop() {
      playing = false;
      if (timer) { clearInterval(timer); timer = null; }
      if (audio) { try { audio.pause(); audio.src = ""; } catch {} audio = null; }
    },
  };
}

const ringer = createRinger();
// Countdown component — display only, expiration is handled by a parent useEffect
function Countdown({ createdAt, notifiedAt, timeoutSeconds = 60 }) {
  const [secs, setSecs] = useState(timeoutSeconds);
  useEffect(() => {
    const t0 = new Date(notifiedAt || createdAt).getTime();
    const tick = () => {
      const elapsed = (Date.now() - t0) / 1000;
      setSecs(Math.max(0, timeoutSeconds - Math.floor(elapsed)));
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [createdAt, notifiedAt, timeoutSeconds]);
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-mono font-bold ${secs <= 15 ? "text-red-500 animate-pulse" : "text-[#646964]"}`}>
      <Timer size={12} /> {secs}s
    </span>
  );
}

// Browser push notification (native OS popup)
  function pushNotify(title, body) {
    if (!("Notification" in window)) return;
    if (Notification.permission === "granted") {
      try {
        new Notification(title, { body, tag: "new-booking" });
      } catch(e) {
        console.error(e);
      }
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then(perm => {
        if (perm === "granted") {
          try {
            new Notification(title, { body, tag: "new-booking" });
          } catch(e) { console.error(e); }
        }
      });
    }
  }

const statusColor = {
  pending: "bg-[#F59E0B]/15 text-[#B45309]",
  accepted: "bg-[#1C4532]/15 text-[#1C4532]",
  on_the_way: "bg-[#D9734E]/15 text-[#C26240]",
  completed: "bg-[#22C55E]/15 text-[#15803D]",
  rejected: "bg-[#94A3B8]/15 text-[#475569]",
  cancelled: "bg-[#94A3B8]/15 text-[#475569]",
};

export default function WorkerDashboard() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [activeTab, setActiveTab] = useState(params.get("tab") || "incoming");
  const { setCounts } = useBookingCounts();

  useEffect(() => {
    const tab = params.get("tab") || "incoming";
    setActiveTab(prev => prev !== tab ? tab : prev);
  }, [params]);
  const [bookings, setBookings] = useState([]);
  const [coords, setCoords] = useState(user?.lat && user?.lng ? { lat: user.lat, lng: user.lng } : null);
  const [online, setOnline] = useState(!!user?.is_online);
  const [services, setServices] = useState([]);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("hg_sound") !== "off");

  const [chatBookingId, setChatBookingId] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const openChat = (id) => { setChatBookingId(id); setChatOpen(true); };
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [reUploads, setReUploads] = useState({});
  const [reUploading, setReUploading] = useState(null);
  const [reSubmitting, setReSubmitting] = useState(false);
  const [fieldEdits, setFieldEdits] = useState({});
  const [razorpayLoading, setRazorpayLoading] = useState(false);
  const [regFee, setRegFee] = useState(99);
  const [schedule, setSchedule] = useState(null);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const photoFileRef = useRef(null);
  const [photoTarget, setPhotoTarget] = useState(null);
  const fileRef = useRef(null);
  const [fileTarget, setFileTarget] = useState(null);

  useEffect(() => {
    if (user?.role === "worker" && !user.verified) {
      api.get("/worker/verification-status").then(r => setVerificationStatus(r.data)).catch(() => {});
      api.get("/content").then(r => { if (r.data?.registration_fee) setRegFee(r.data.registration_fee); }).catch(() => {});
    }
    if (services.length === 0) {
      api.get("/services").then(r => setServices(r.data)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (user?.verified && !sessionStorage.getItem("hg_celebration_shown")) {
      sessionStorage.setItem("hg_celebration_shown", "1");
      setShowCelebration(true);
    }
  }, [user?.verified]);

  useEffect(() => {
    if (user?.role === "worker" && user.verified) {
      api.get("/worker/schedule").then(r => setSchedule(r.data)).catch(() => {});
    }
  }, [user?.verified, user?.role]);

  const updateSchedule = async () => {
    if (!schedule) return;
    setSavingSchedule(true);
    try {
      await api.put("/worker/schedule", schedule);
      toast.success("Schedule saved");
    } catch { toast.error("Failed to save schedule"); }
    finally { setSavingSchedule(false); }
  };

  const toggleScheduleDay = (day) => {
    setSchedule(p => ({ ...p, [day]: { ...p[day], enabled: !p[day]?.enabled } }));
  };

  const updateScheduleTime = (day, field, val) => {
    setSchedule(p => ({ ...p, [day]: { ...p[day], [field]: val } }));
  };

  const handlePhotoUpload = async (e, bookingId, photoType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(bookingId + photoType);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const base = API.replace(/\/api$/, "");
      const res = await fetch(`${base}/api/upload`, { method: "POST", body: fd });
      const data = await res.json();
      if (data.ok && data.url) {
        const pos = await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
            () => resolve({}),
            { timeout: 3000 }
          );
        });
        await api.post(`/bookings/${bookingId}/photos`, { photo_type: photoType, url: data.url, ...pos });
        toast.success(`${photoType === "start" ? "Start" : "Complete"} photo uploaded`);
        loadBookings();
      } else { toast.error("Upload failed"); }
    } catch { toast.error("Upload failed"); }
    finally { setUploadingPhoto(null); e.target.value = ""; }
  };

  const handleReUpload = async (e) => {
    const file = e.target.files?.[0];
    const target = fileTarget;
    if (!file || !target) return;
    setReUploading(target);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const base = API.replace(/\/api$/, "");
      const res = await fetch(`${base}/api/upload`, { method: "POST", body: fd });
      const data = await res.json();
      if (data.ok && data.url) {
        setReUploads(p => ({ ...p, [target]: data.url }));
        toast.success("Uploaded");
      } else {
        toast.error("Upload failed");
      }
    } catch {
      toast.error("Upload failed");
    } finally {
      setReUploading(null);
      setFileTarget(null);
      e.target.value = "";
    }
  };

  const reSubmitVerification = async () => {
    setReSubmitting(true);
    try {
      const body = {};
      if (reUploads.aadharUrl) body.aadharUrl = reUploads.aadharUrl;
      if (reUploads.panUrl) body.panUrl = reUploads.panUrl;
      if (reUploads.paymentScreenshot) body.paymentScreenshotUrl = reUploads.paymentScreenshot;
      // Include edited profile fields
      if (fieldEdits.name) body.name = fieldEdits.name;
      if (fieldEdits.address) body.address = fieldEdits.address;
      if (fieldEdits.phone) body.phone = fieldEdits.phone;
      if (fieldEdits.price != null) body.price = Number(fieldEdits.price);
      if (fieldEdits.service_id) body.service_id = fieldEdits.service_id;
      await api.post("/worker/submit-verification", body);
      if (reUploads.profilePic) {
        await api.put("/users/me", { profile_pic: reUploads.profilePic }).catch(() => {});
      }
      toast.success("Re-submitted for verification");
      api.get("/worker/verification-status").then(r => setVerificationStatus(r.data)).catch(() => {});
      setReUploads({});
      setFieldEdits({});
    } catch (err) {
      toast.error("Failed to re-submit");
    } finally {
      setReSubmitting(false);
    }
  };

  const payRegistrationFee = async () => {
    setRazorpayLoading(true);
    try {
      await api.post("/worker/pay-and-verify", {
        razorpay_order_id: `fake_${Date.now()}`,
        razorpay_payment_id: `fake_pay_${Date.now()}`,
      });
      toast.success("Payment successful! Documents submitted for review.");
      setRazorpayLoading(false);
      api.get("/worker/verification-status").then(r => setVerificationStatus(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.error || "Submission failed");
      setRazorpayLoading(false);
    }
  };

  const rejectionReasons = verificationStatus?.rejection_reason ? verificationStatus.rejection_reason.split("; ").map(s => s.trim()) : [];
  const alreadyPaid = verificationStatus?.payment_status === "success";
  const needsProfilePic = rejectionReasons.includes("Profile picture not valid");
  const needsAadhar = rejectionReasons.includes("Aadhar not valid");
  const needsPayment = rejectionReasons.includes("Payment not valid") && !alreadyPaid;
  const needsName = rejectionReasons.includes("Name not valid");
  const needsAddress = rejectionReasons.includes("Address not valid");
  const needsPhone = rejectionReasons.includes("Phone not valid");
  const needsPrice = rejectionReasons.includes("Price not valid");
  const needsService = rejectionReasons.includes("Service not valid");

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("hg_sound", next ? "on" : "off");
    if (next) {
      registerPush().then((res) => {
        if (res !== true) toast.error(`Push: ${res}`, { duration: 10000 });
      });
      try { const a = new Audio("/notification.mp3"); a.volume = 0.5; a.play().catch(() => {}); } catch {}
      toast.success(t("workerDashboard.soundOn"));
    } else {
      unregisterPush();
      toast.info(t("workerDashboard.soundOff"));
    }
  };

  const prevIdsRef = useRef(null);

  const loadBookings = useCallback(async () => {
    const r = await api.get("/bookings/mine");
    const fresh = r.data;
    const ids = new Set(fresh.map(b => b.id));
    if (prevIdsRef.current) {
      fresh.forEach(b => {
        if (b.status === "pending" && user?.verified && !prevIdsRef.current.has(b.id) && !takenRef.current.has(b.id) && !expiredRef.current.has(b.id)) {
          if (localStorage.getItem("hg_sound") !== "off") ringer.start();
          pushNotify(t("workerDashboard.newBooking"), `${b.service_name}\n${b.address || ""}`);
          toast.success(`${t("workerDashboard.newBooking")} ${b.service_name}`, { duration: 10000 });
        }
      });
    }
    // Silent-expire: immediately add already-expired bookings so Countdown never toasts them
    const now = Date.now();
    const expiredNow = new Set();
    fresh.forEach(b => {
      if (b.status !== "pending") return;
      const t0 = new Date(b.notified_at || b.created_at).getTime();
      const timeout = b.timeout_seconds || 60;
      if (now - t0 >= timeout * 1000) expiredNow.add(b.id);
    });
    // Also sync with expiredRef for bookings already expired by central useEffect
    if (expiredNow.size) {
      expiredNow.forEach(id => expiredRef.current.add(id));
      setExpiredIds(prev => { const n = new Set(prev); expiredNow.forEach(id => n.add(id)); return n; });
    }

    prevIdsRef.current = ids;
    setBookings(fresh);
  }, [t, user]);

  // Auto-refresh booking list every 8s
  useAutoRefresh(() => { if (user) loadBookings(); }, [user?.id], 8000);

  useEffect(() => {
    if (!user) return;
    loadBookings();
    api.get("/services").then(r => setServices(r.data));
    if (localStorage.getItem("hg_sound") !== "off") registerPush();

    let ws = null;
    let reconnectTimer = null;
    function connect() {
      if (ws) try { ws.close(); } catch {}
      ws = new WebSocket(`${WS_URL}/api/ws/${user.id}`);
      ws.onopen = () => {};
      ws.onerror = () => {};
      ws.onclose = () => {
        reconnectTimer = setTimeout(connect, 2000);
      };
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === "new_booking") {
            if (localStorage.getItem("hg_sound") !== "off") {
              ringer.start();
            }
            setTakenIds(prev => { const n = new Set(prev); n.delete(data.booking.id); return n; });
            setExpiredIds(prev => { const n = new Set(prev); n.delete(data.booking.id); return n; });
            pushNotify(t("workerDashboard.newBooking"), `${data.booking.service_name}\n${data.booking.address || ""}`);
            toast.success(`${t("workerDashboard.newBooking")} ${data.booking.service_name}`, { duration: 10000 });
            loadBookings();
          }
          if (data.type === "booking_taken") {
            ringer.stop();
            setTakenIds(prev => new Set([...prev, data.bookingId]));
            loadBookings();
          }
        } catch(e) { /* ignore */ }
      };
    }
    connect();
    return () => { clearTimeout(reconnectTimer); if (ws) ws.close(); };
    // eslint-disable-next-line
  }, [user]);

  const toggleOnline = async (val) => {
    setOnline(val);
    let updates = { is_online: val };
    if (val && !coords) {
      // request location when going online
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setCoords(c);
          await api.put("/workers/me", { is_online: true, lat: c.lat, lng: c.lng });
          await refresh();
          toast.success(t("workerDashboard.onlineWithLocation"));
        },
        async () => {
          await api.put("/workers/me", updates);
          await refresh();
          toast.warning(t("workerDashboard.onlineNoLocation"));
        }
      );
    } else {
      if (coords && val) { updates.lat = coords.lat; updates.lng = coords.lng; }
      await api.put("/workers/me", updates);
      await refresh();
      toast.success(val ? t("workerDashboard.nowOnline") : t("workerDashboard.nowOffline"));
    }
  };

  // Auto-update location every 3s when online
  useEffect(() => {
    if (!online || !user?.verified) return;
    const id = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setCoords(c);
          api.put("/workers/me", { lat: c.lat, lng: c.lng }).catch(() => {});
        },
        () => {},
        { enableHighAccuracy: true, timeout: 5000 }
      );
    }, 3000);
    return () => clearInterval(id);
  }, [online, user?.verified]);

  const updateLocation = () => {
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCoords(c);
        await api.put("/workers/me", { lat: c.lat, lng: c.lng });
        await refresh();
        toast.success(t("workerDashboard.locationUpdated"));
      },
      () => toast.error(t("workerDashboard.locationDenied"))
    );
  };

  const [takenIds, setTakenIds] = useState(new Set());
  const [expiredIds, setExpiredIds] = useState(new Set());
  const takenRef = useRef(takenIds);
  const expiredRef = useRef(expiredIds);
  takenRef.current = takenIds;
  expiredRef.current = expiredIds;

  // Play sound whenever pending bookings appear (initial load, refresh, new via WS/poll)
  useEffect(() => {
    if (!user?.verified) return;
    const hasPending = bookings.some(b => b.status === "pending" && !takenIds.has(b.id) && !expiredIds.has(b.id));
    if (hasPending && localStorage.getItem("hg_sound") !== "off") {
      ringer.start();
    } else if (!hasPending) ringer.stop();
  }, [bookings, takenIds, expiredIds, user?.verified]);

  const respond = async (bid, status) => {
    if (status === "rejected") {
      ringer.stop();
      try {
        await api.put(`/bookings/${bid}/status`, { status: "rejected" });
        toast.info(t("workerDashboard.rejected"));
      } catch { /* server may reject if already accepted */ }
      loadBookings();
      return;
    }
    try {
      await api.put(`/bookings/${bid}/status`, { status });
      ringer.stop();
      toast.success(`${t("workerDashboard.bookingStatus")} ${status.replace(/_/g, " ")}`);
      loadBookings();
    } catch (e) {
      const msg = e?.response?.data?.detail || "";
      if (msg.includes("already accepted") || msg.includes("another worker")) {
        ringer.stop();
        setTakenIds(prev => new Set([...prev, bid]));
        toast.info("Booking was accepted by another worker");
        loadBookings();
      } else {
        toast.error(e?.response?.data?.detail || "Failed");
      }
    }
  };

  // Central expiry detection — runs every second + when bookings/taken/expired change
  const loadBookingsRef = useRef(loadBookings);
  loadBookingsRef.current = loadBookings;
  useEffect(() => {
    if (!user?.verified) return;
    const check = () => {
      const now = Date.now();
      const toExpire = [];
      for (const b of bookings) {
        if (b.status !== "pending") continue;
        if (takenIds.has(b.id) || expiredIds.has(b.id)) continue;
        const t0 = new Date(b.notified_at || b.created_at).getTime();
        if (now - t0 >= (b.timeout_seconds || 60) * 1000) toExpire.push(b.id);
      }
      if (!toExpire.length) return false;
      toExpire.forEach(bid => {
        if (expiredRef.current.has(bid)) return;
        setExpiredIds(prev => new Set([...prev, bid]));
        toast.info("Booking offer expired");
      });
      ringer.stop();
      loadBookingsRef.current();
      return true;
    };
    check();
    const iv = setInterval(check, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, takenIds, expiredIds, user?.verified]);

  useEffect(() => {
    setCounts({
      pending: bookings.filter(b => b.status === "pending" && !takenIds.has(b.id) && !expiredIds.has(b.id)).length,
      active: bookings.filter(b => ["accepted", "on_the_way"].includes(b.status)).length,
    });
  }, [bookings, takenIds, expiredIds, setCounts]);

  if (!user) return null;

  const pending = bookings.filter(b =>
    b.status === "pending" && !takenIds.has(b.id) && !expiredIds.has(b.id)
  );
  const active = bookings.filter(b => ["accepted", "on_the_way"].includes(b.status));
  const past = bookings.filter(b => ["completed", "rejected", "cancelled"].includes(b.status) || expiredIds.has(b.id) || takenIds.has(b.id));

  const myService = services.find(s => s.id === user.service_id);

  return (
    <><Seo title="Worker Dashboard" description="Manage your jobs, availability, and earnings on Universal Tree." noindex />
    <div className="min-h-screen flex flex-col bg-[#F8F7F4]">
      <Navbar />
      <main className="flex-1 max-w-screen-2xl mx-auto px-4 sm:px-6 py-6 md:py-10 w-full">
        {!user.verified ? (
          verificationStatus?.status === "rejected" ? (
            <div className="max-w-2xl mx-auto">
              <input type="file" accept="image/*" ref={fileRef} onChange={handleReUpload} className="hidden" />
              <div className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)] p-8 md:p-10 text-center border border-red-100">
                <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-5">
                  <AlertTriangle size={28} className="text-[#DC2626]" />
                </div>
                <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1D1A] mb-2">Application Rejected</h2>
                <p className="text-[#646964] mb-1">Your application was not approved.</p>
                {verificationStatus?.rejection_reason && (
                  <div className="mt-4 inline-flex items-center gap-2 bg-red-50 text-[#DC2626] text-sm font-medium px-4 py-2 rounded-lg">
                    <AlertTriangle size={14} />
                    <span>{verificationStatus.rejection_reason}</span>
                  </div>
                )}
                <p className="text-sm text-[#D9734E] mt-5">Fix the issues below and re-submit.</p>
              </div>

              {(needsName || needsAddress || needsPhone || needsPrice || needsService) && (
                <div className="mt-5 space-y-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#D9734E]">Profile Fields</p>
                  {needsName && (
                    <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-5">
                      <label className="text-xs font-medium text-[#646964] mb-1.5 block">Full Name</label>
                      <input value={fieldEdits.name ?? user.name} onChange={e => setFieldEdits(p => ({...p, name: e.target.value}))} className="w-full rounded-lg border border-[#E2E0D9] bg-white h-11 px-3 text-sm outline-none focus:ring-2 focus:ring-[#1C4532]/20" placeholder="Enter your name" />
                    </div>
                  )}
                  {needsAddress && (
                    <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-5">
                      <label className="text-xs font-medium text-[#646964] mb-1.5 block">Address</label>
                      <input value={fieldEdits.address ?? user.address} onChange={e => setFieldEdits(p => ({...p, address: e.target.value}))} className="w-full rounded-lg border border-[#E2E0D9] bg-white h-11 px-3 text-sm outline-none focus:ring-2 focus:ring-[#1C4532]/20" placeholder="Enter your address" />
                    </div>
                  )}
                  {needsPhone && (
                    <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-5">
                      <label className="text-xs font-medium text-[#646964] mb-1.5 block">Phone</label>
                      <input value={fieldEdits.phone ?? user.phone} onChange={e => setFieldEdits(p => ({...p, phone: e.target.value}))} className="w-full rounded-lg border border-[#E2E0D9] bg-white h-11 px-3 text-sm outline-none focus:ring-2 focus:ring-[#1C4532]/20" placeholder="Enter your phone number" />
                    </div>
                  )}
                  {needsPrice && (
                    <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-5">
                      <label className="text-xs font-medium text-[#646964] mb-1.5 block">Service Price (₹)</label>
                      <input type="number" value={fieldEdits.price ?? user.price} onChange={e => setFieldEdits(p => ({...p, price: e.target.value}))} className="w-full rounded-lg border border-[#E2E0D9] bg-white h-11 px-3 text-sm outline-none focus:ring-2 focus:ring-[#1C4532]/20" placeholder="Enter your price" />
                    </div>
                  )}
                  {needsService && (
                    <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-5">
                      <label className="text-xs font-medium text-[#646964] mb-1.5 block">Service</label>
                      <select value={fieldEdits.service_id ?? user.service_id} onChange={e => setFieldEdits(p => ({...p, service_id: e.target.value}))} className="w-full rounded-lg border border-[#E2E0D9] bg-white h-11 px-3 text-sm outline-none focus:ring-2 focus:ring-[#1C4532]/20">
                        <option value="">Select a service</option>
                        {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {(needsProfilePic || needsAadhar || needsPayment) && (
                <div className="mt-5 space-y-4">
                  {needsProfilePic && (
                    <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-5">
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#D9734E] mb-4">Profile Photo</p>
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-dashed border-[#E2E0D9] bg-[#F8F7F4] flex items-center justify-center shrink-0">
                          {reUploads.profilePic ? <img src={reUploads.profilePic} alt="" className="w-full h-full object-cover" /> : <Upload size={20} className="text-[#94A3B8]" />}
                        </div>
                        <button onClick={() => { setFileTarget("profilePic"); fileRef.current?.click(); }} disabled={reUploading === "profilePic"} className="rounded-lg bg-[#1C4532] hover:bg-[#143324] text-white px-5 h-10 text-sm font-medium transition-colors">
                          {reUploading === "profilePic" ? "Uploading..." : "Upload photo"}
                        </button>
                      </div>
                    </div>
                  )}
                  {needsAadhar && (
                    <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-5">
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#D9734E] mb-4">Aadhar / PAN Card</p>
                      <div className="flex items-center gap-4">
                        <div className="w-24 h-16 rounded-lg overflow-hidden border-2 border-dashed border-[#E2E0D9] bg-[#F8F7F4] flex items-center justify-center shrink-0">
                          {reUploads.aadharUrl ? <img src={reUploads.aadharUrl} alt="" className="w-full h-full object-cover" /> : <Upload size={20} className="text-[#94A3B8]" />}
                        </div>
                        <button onClick={() => { setFileTarget("aadharUrl"); fileRef.current?.click(); }} disabled={reUploading === "aadharUrl"} className="rounded-lg bg-[#1C4532] hover:bg-[#143324] text-white px-5 h-10 text-sm font-medium transition-colors">
                          {reUploading === "aadharUrl" ? "Uploading..." : "Upload"}
                        </button>
                      </div>
                    </div>
                  )}
                  {needsPayment && (
                    <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-5">
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#D9734E] mb-4">Payment Screenshot</p>
                      <div className="flex items-center gap-4">
                        <div className="w-24 h-16 rounded-lg overflow-hidden border-2 border-dashed border-[#E2E0D9] bg-[#F8F7F4] flex items-center justify-center shrink-0">
                          {reUploads.paymentScreenshot ? <img src={reUploads.paymentScreenshot} alt="" className="w-full h-full object-cover" /> : <Upload size={20} className="text-[#94A3B8]" />}
                        </div>
                        <button onClick={() => { setFileTarget("paymentScreenshot"); fileRef.current?.click(); }} disabled={reUploading === "paymentScreenshot"} className="rounded-lg bg-[#1C4532] hover:bg-[#143324] text-white px-5 h-10 text-sm font-medium transition-colors">
                          {reUploading === "paymentScreenshot" ? "Uploading..." : "Upload"}
                        </button>
                      </div>
                    </div>
                  )}
                  <button onClick={reSubmitVerification} disabled={reSubmitting} className="w-full rounded-xl bg-[#1C4532] hover:bg-[#143324] text-white font-semibold py-3.5 px-6 transition-colors disabled:opacity-50">
                    {reSubmitting ? "Submitting..." : alreadyPaid ? "Re-submit for Verification" : "Re-submit for Verification"}
                  </button>
                </div>
              )}
            </div>
          ) : verificationStatus?.status === "draft" ? (
            <div className="max-w-lg mx-auto">
              <div className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)] p-8 md:p-10 text-center">
                <div className="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center mx-auto mb-5">
                  <CreditCard size={28} className="text-[#D9734E]" />
                </div>
                <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1D1A] mb-2">Payment Pending</h2>
                <p className="text-[#646964] leading-relaxed mb-6">Your documents have been saved. Pay the registration fee to submit them for admin review.</p>
                <button onClick={payRegistrationFee} disabled={razorpayLoading} className="w-full rounded-xl bg-[#1C4532] hover:bg-[#143324] text-white font-semibold py-3.5 px-6 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {razorpayLoading ? <><Loader2 size={16} className="animate-spin" /> Processing...</> : <><IndianRupee size={16} /> Pay ₹{regFee} Registration Fee</>}
                </button>
                <p className="text-xs text-[#94A3B8] mt-4">Registration fee payment</p>
              </div>
            </div>
          ) : verificationStatus?.status === "pending" ? (
            <div className="max-w-lg mx-auto">
              <div className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)] p-8 md:p-10 text-center">
                <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-5">
                  <Clock size={28} className="text-[#D97706]" />
                </div>
                <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1D1A] mb-2">Verification in Progress</h2>
                <p className="text-[#646964] leading-relaxed mb-4">Your documents have been submitted for admin review. Once verified, you&apos;ll get full access to the dashboard and start receiving bookings.</p>
                <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 text-sm font-medium px-4 py-2 rounded-lg">
                  <Clock size={14} />
                  <span>Typically takes 1–2 business days</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-lg mx-auto">
              <div className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)] p-8 md:p-10 text-center">
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-5">
                  <Upload size={28} className="text-[#1C4532]" />
                </div>
                <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1D1A] mb-2">Complete Registration</h2>
                <p className="text-[#646964] leading-relaxed mb-4">Please complete your registration by uploading your documents and paying the registration fee.</p>
                <button onClick={() => nav("/signup?resume=1")} className="rounded-xl bg-[#1C4532] hover:bg-[#143324] text-white font-semibold py-3.5 px-6 transition-colors">Continue Registration</button>
              </div>
            </div>
          )
        ) : (
        <>
        {/* ── Greeting header ── */}
        <div className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)] p-5 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="flex items-center gap-4 md:gap-5 min-w-0">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-[#1C4532]/10 flex items-center justify-center shrink-0 text-[#1C4532] font-semibold text-lg md:text-xl">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-widest text-[#D9734E] font-semibold">{t("workerDashboard.badge")}</p>
                <h1 className="text-xl md:text-3xl font-bold text-[#1A1D1A] mt-0.5 truncate">Hello, {user.name.split(" ")[0]}.</h1>
                <p className="text-sm text-[#646964] mt-0.5 truncate">
                  {myService?.name || "Service not set"}
                  <span className="mx-2 text-[#E2E0D9]">·</span>
                  <IndianRupee size={12} className="inline" />{user.price || 0}
                  <span className="mx-2 text-[#E2E0D9]">·</span>
                  <Star size={12} className="inline text-[#D9734E]" /> {user.rating?.toFixed?.(1) || "5.0"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 md:gap-5">
              <button
                onClick={toggleSound}
                data-testid="sound-toggle"
                title={soundOn ? "Mute notifications" : "Unmute notifications"}
                className={`w-10 h-10 rounded-xl grid place-items-center transition-colors ${soundOn ? "bg-[#1C4532] text-white shadow-sm" : "bg-[#F3F1ED] text-[#646964] hover:bg-[#E8E6E0]"}`}
              >
                {soundOn ? <Volume2 size={16}/> : <VolumeX size={16}/>}
              </button>
              <div className="flex items-center gap-3 bg-[#F8F7F4] rounded-xl px-4 py-2.5">
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wider text-[#646964] font-medium">{t("workerDashboard.status")}</p>
                  <p className="text-sm font-semibold text-[#1A1D1A] flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${online ? "bg-[#22C55E]" : "bg-[#94A3B8]"}`}></span>
                    {online ? t("common.online") : t("common.offline")}
                  </p>
                </div>
                <Switch checked={online} onCheckedChange={toggleOnline} data-testid="online-toggle" className="data-[state=checked]:bg-[#1C4532]" />
              </div>
            </div>
          </div>

          {/* Stats row — visible on mobile + desktop */}
          <div className="mt-5 pt-5 border-t border-[#F3F1ED] grid grid-cols-3 gap-3">
            <div className="bg-[#F8F7F4] rounded-xl p-3 md:p-4 text-center">
              <p className="text-xs text-[#646964] font-medium">Service</p>
              <p className="text-sm md:text-base font-semibold text-[#1A1D1A] mt-0.5 truncate">{myService?.name || "—"}</p>
            </div>
            <div className="bg-[#F8F7F4] rounded-xl p-3 md:p-4 text-center">
              <p className="text-xs text-[#646964] font-medium">Rating</p>
              <p className="text-sm md:text-base font-semibold text-[#1A1D1A] mt-0.5"><Star size={14} className="inline text-[#D9734E] mr-0.5" />{user.rating?.toFixed?.(1) || "5.0"}</p>
            </div>
            <div className="bg-[#F8F7F4] rounded-xl p-3 md:p-4 text-center">
              <p className="text-xs text-[#646964] font-medium">Jobs Done</p>
              <p className="text-sm md:text-base font-semibold text-[#1A1D1A] mt-0.5">{user.jobs_completed || 0}</p>
            </div>
          </div>
        </div>

        {/* ── Tab content ── */}
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); nav(`/worker?tab=${v}`, { replace: true }); }} className="mt-6 md:mt-8">

          {/* ── INCOMING ── */}
          <TabsContent value="incoming" className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pending.length === 0 && (
              <div className="col-span-full py-16 text-center">
                <Bell size={40} className="mx-auto text-[#E2E0D9] mb-4" />
                <p className="text-[#94A3B8] font-medium">{t("workerDashboard.noIncoming")}</p>
              </div>
            )}
            {pending.map(b => (
              <div key={b.id} data-testid={`incoming-${b.id}`} className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)] border-l-4 border-l-[#D9734E] overflow-hidden">
                <div className="p-5 md:p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2.5">
                        <span className="w-2 h-2 rounded-full bg-[#D9734E] animate-pulse shrink-0"></span>
                        <p className="font-semibold text-[#1A1D1A] truncate">{b.service_name}</p>
                      </div>
                      <p className="text-xs text-[#94A3B8] font-mono mt-1.5 flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wider text-[#94A3B8]">#{b.id.slice(-8)}</span>
                        {b.created_at && Date.now() - new Date(b.notified_at || b.created_at).getTime() < (b.timeout_seconds || 60) * 1000 && <Countdown createdAt={b.created_at} notifiedAt={b.notified_at} timeoutSeconds={b.timeout_seconds || 60} />}
                      </p>
                    </div>
                    <Badge className={`${statusColor[b.status]} rounded-full text-[10px] font-semibold px-2.5 py-0.5 shrink-0`}>{b.status}</Badge>
                  </div>

                  <div className="mt-4 space-y-2 text-sm text-[#646964]">
                    <div className="flex items-start gap-2.5">
                      <MapPin size={14} className="mt-0.5 shrink-0 text-[#94A3B8]" />
                      <span className="line-clamp-1">{b.address}</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <Phone size={14} className="shrink-0 text-[#94A3B8]" />
                      <span>{b.phone}</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <IndianRupee size={14} className="shrink-0 text-[#94A3B8]" />
                      <span className="font-semibold text-[#1C4532]">₹{b.amount}</span>
                    </div>
                  </div>

                  {b.notes && (
                    <p className="mt-3 text-sm text-[#94A3B8] italic line-clamp-2 border-l-2 border-[#F3F1ED] pl-3">&ldquo;{b.notes}&rdquo;</p>
                  )}

                  <div className="mt-5 flex gap-3">
                    <button onClick={() => respond(b.id, "accepted")} data-testid={`accept-${b.id}`} className="flex-1 rounded-xl bg-[#D9734E] hover:bg-[#C26240] text-white text-sm font-semibold h-12 transition-colors flex items-center justify-center gap-2 shadow-sm">
                      <CheckCircle2 size={16} /> Accept →
                    </button>
                    <button onClick={() => respond(b.id, "rejected")} data-testid={`reject-${b.id}`} className="flex-1 rounded-xl border border-[#E2E0D9] text-[#64748B] hover:bg-[#F8F7F4] text-sm font-medium h-12 transition-colors flex items-center justify-center">
                      Decline
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>

          {/* ── ACTIVE ── */}
          <TabsContent value="active" className="mt-5 md:mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {active.length === 0 && (
              <div className="col-span-full py-16 text-center">
                <Activity size={40} className="mx-auto text-[#E2E0D9] mb-4" />
                <p className="text-[#94A3B8] font-medium">{t("workerDashboard.noActive")}</p>
              </div>
            )}
            {active.map(b => (
              <div key={b.id} data-testid={`active-${b.id}`} className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)] overflow-hidden">
                <div className="p-5 md:p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-[#1A1D1A] truncate">{b.service_name}</p>
                      <p className="text-xs text-[#94A3B8] mt-0.5 truncate">{b.full_name} · {b.phone}</p>
                    </div>
                    <Badge className={`${statusColor[b.status]} rounded-full text-[10px] font-semibold px-2.5 py-0.5 shrink-0`}>{b.status.replace(/_/g, " ")}</Badge>
                  </div>

                  <div className="mt-3 flex items-start gap-2.5 text-sm text-[#646964]">
                    <MapPin size={14} className="mt-0.5 shrink-0 text-[#94A3B8]" />
                    <span className="line-clamp-1">{b.address}</span>
                  </div>
                  {b.notes && <p className="mt-2 text-sm text-[#94A3B8] italic line-clamp-2 border-l-2 border-[#F3F1ED] pl-3">&ldquo;{b.notes}&rdquo;</p>}
                  <p className="mt-3 text-lg font-bold text-[#1C4532]">₹{b.amount}</p>
                </div>

                {b.lat != null && b.lng != null && !chatOpen && (
                  <div className="border-t border-[#F3F1ED]">
                    <MapView
                      center={[b.lat, b.lng]}
                      userMarker={[b.lat, b.lng]}
                      workers={coords ? [{ id: `me-${b.id}`, name: "You", lat: coords.lat, lng: coords.lng, price: user.price, rating: user.rating, is_online: true }] : []}
                      height={180}
                      zoom={13}
                    />
                  </div>
                )}

                <div className="px-5 md:px-6 py-3.5 border-t border-[#F3F1ED] flex flex-wrap items-center gap-2">
                  <a href={`tel:${b.phone}`} data-testid={`call-${b.id}`}>
                    <button className="rounded-lg border border-[#E2E0D9] hover:bg-[#F8F7F4] text-xs font-medium text-[#646964] h-9 px-3.5 transition-colors flex items-center gap-1.5">
                      <Phone size={13} /><span className="hidden sm:inline">{t("workerDashboard.call")}</span>
                    </button>
                  </a>
                  <button onClick={() => openChat(b.id)} data-testid={`chat-${b.id}`} className="rounded-lg border border-[#E2E0D9] hover:bg-[#F8F7F4] text-xs font-medium text-[#646964] h-9 px-3.5 transition-colors flex items-center gap-1.5">
                    <MessageCircle size={13} /><span className="hidden sm:inline">{t("workerDashboard.chat")}</span>
                  </button>
                  {b.lat != null && b.lng != null && (
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${b.lat},${b.lng}&travelmode=driving`} target="_blank" rel="noopener noreferrer">
                      <button className="rounded-lg border border-[#1C4532] text-[#1C4532] hover:bg-[#1C4532] hover:text-white text-xs font-medium h-9 px-3.5 transition-colors flex items-center gap-1.5">
                        <Navigation size={13} /><span className="hidden sm:inline">{t("workerDashboard.directions")}</span>
                      </button>
                    </a>
                  )}
                  <div className="ml-auto flex gap-2">
                    {b.status !== "completed" && (
                      <button onClick={() => { setPhotoTarget(b.id + "-start"); photoFileRef.current?.click(); }}
                        disabled={uploadingPhoto === b.id + "-start"}
                        className="rounded-lg border border-[#E2E0D9] hover:bg-[#F8F7F4] text-xs font-medium text-[#646964] h-9 px-3 transition-colors flex items-center gap-1 disabled:opacity-50">
                        {uploadingPhoto === b.id + "-start" ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
                        <span className="hidden sm:inline">Start</span>
                      </button>
                    )}
                    {b.status === "on_the_way" && (
                      <button onClick={() => { setPhotoTarget(b.id + "-complete"); photoFileRef.current?.click(); }}
                        disabled={uploadingPhoto === b.id + "-complete"}
                        className="rounded-lg border border-[#E2E0D9] hover:bg-[#F8F7F4] text-xs font-medium text-[#646964] h-9 px-3 transition-colors flex items-center gap-1 disabled:opacity-50">
                        {uploadingPhoto === b.id + "-complete" ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
                        <span className="hidden sm:inline">Complete</span>
                      </button>
                    )}
                    {b.status === "accepted" && <button onClick={() => respond(b.id, "on_the_way")} data-testid={`on-way-${b.id}`} className="rounded-lg bg-[#D9734E] hover:bg-[#C26240] text-white text-xs font-semibold h-9 px-4 transition-colors">{t("workerDashboard.onWay")}</button>}
                    {b.status === "on_the_way" && <button onClick={() => respond(b.id, "completed")} data-testid={`done-${b.id}`} className="rounded-lg bg-[#1C4532] hover:bg-[#143324] text-white text-xs font-semibold h-9 px-4 transition-colors">{t("workerDashboard.done")}</button>}
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>

          {/* ── HISTORY ── */}
          <TabsContent value="past" className="mt-5 md:mt-6">
            {past.length === 0 ? (
              <div className="py-16 text-center">
                <Clock size={40} className="mx-auto text-[#E2E0D9] mb-4" />
                <p className="text-[#94A3B8] font-medium">{t("workerDashboard.noHistory")}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {past.map(b => (
                  <div key={b.id} className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-5 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-shadow">
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-semibold text-[#1A1D1A] truncate flex-1 text-sm">{b.service_name}</p>
                      <Badge className={`${statusColor[b.status]} rounded-full text-[10px] font-semibold px-2 py-0.5 shrink-0`}>{b.status}</Badge>
                    </div>
                    <p className="text-xs text-[#94A3B8] mt-2">{new Date(b.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                    <p className="text-lg font-bold text-[#1C4532] mt-2">₹{b.amount}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ── MAP ── */}
          <TabsContent value="map" className="mt-5 md:mt-6">
            <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-[#1A1D1A]">Your Location</h3>
                <button onClick={updateLocation} data-testid="update-location" className="rounded-lg bg-[#1C4532] hover:bg-[#143324] text-white text-xs font-medium h-9 px-4 transition-colors flex items-center gap-1.5">
                  <MapPin size={13} />{t("workerDashboard.updateLocation")}
                </button>
              </div>
              <div className="rounded-xl overflow-hidden border border-[#F3F1ED]">
                <MapView
                  center={coords ? [coords.lat, coords.lng] : [12.9716, 77.5946]}
                  userMarker={coords ? [coords.lat, coords.lng] : null}
                  workers={active.filter(b => b.lat != null && b.lng != null).map(b => ({
                    id: b.id, name: b.service_name || "Booking",
                    lat: b.lat, lng: b.lng,
                    price: b.amount || 0, rating: 5, is_online: true
                  }))}
                  height={500}
                />
              </div>
              {active.filter(b => b.lat != null && b.lng != null).length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {active.filter(b => b.lat != null && b.lng != null).map(b => (
                    <a key={b.id} href={`https://www.google.com/maps/dir/?api=1&destination=${b.lat},${b.lng}&travelmode=driving`}
                      target="_blank" rel="noopener noreferrer"
                      className="rounded-lg border border-[#E2E0D9] hover:bg-[#F8F7F4] text-xs font-medium text-[#646964] h-9 px-3.5 transition-colors flex items-center gap-1.5">
                      <Navigation size={13} />Route to {b.service_name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ── SCHEDULE ── */}
          <TabsContent value="schedule" className="mt-5 md:mt-6">
            <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-4 md:p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-sm font-semibold text-[#1A1D1A] flex items-center gap-2"><Calendar size={16} className="text-[#D9734E]" />Availability Schedule</h3>
                <button onClick={updateSchedule} disabled={savingSchedule || !schedule}
                  className="rounded-lg bg-[#1C4532] hover:bg-[#143324] disabled:opacity-50 text-white text-xs font-medium h-9 px-4 transition-colors flex items-center gap-1.5">
                  <Save size={13} />{savingSchedule ? "Saving..." : "Save"}
                </button>
              </div>
              {schedule ? (
                <div className="space-y-2">
                  {["monday","tuesday","wednesday","thursday","friday","saturday","sunday"].map(day => (
                    <div key={day} className={`flex items-center gap-3 rounded-xl p-3 transition-colors ${schedule[day]?.enabled ? "bg-[#F8F7F4]" : "bg-[#F3F1ED] opacity-60"}`}>
                      <button onClick={() => toggleScheduleDay(day)}
                        className={`w-8 h-8 rounded-lg grid place-items-center text-xs font-bold transition-colors ${schedule[day]?.enabled ? "bg-[#1C4532] text-white" : "bg-white text-[#94A3B8] border border-[#E2E0D9]"}`}>
                        <CheckCircle2 size={14} />
                      </button>
                      <span className="text-sm font-semibold text-[#1A1D1A] w-24 capitalize">{day}</span>
                      <div className="flex items-center gap-2 ml-auto">
                        <input type="time" value={schedule[day]?.start || "09:00"} onChange={e => updateScheduleTime(day, "start", e.target.value)}
                          disabled={!schedule[day]?.enabled} className="text-xs border border-[#E2E0D9] rounded-lg px-2.5 py-1.5 w-24 bg-white" />
                        <span className="text-xs text-[#94A3B8]">to</span>
                        <input type="time" value={schedule[day]?.end || "18:00"} onChange={e => updateScheduleTime(day, "end", e.target.value)}
                          disabled={!schedule[day]?.enabled} className="text-xs border border-[#E2E0D9] rounded-lg px-2.5 py-1.5 w-24 bg-white" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-[#94A3B8]"><Loader2 size={20} className="animate-spin mx-auto mb-2" />Loading schedule...</div>
              )}
            </div>
          </TabsContent>
        </Tabs>
        </>
        )}
      </main>
      <input ref={photoFileRef} type="file" accept="image/*" onChange={e => {
        if (photoTarget) {
          const [bid, ptype] = photoTarget.split("-");
          handlePhotoUpload(e, bid, ptype);
          setPhotoTarget(null);
        }
      }} hidden />
      <Dialog open={showCelebration} onOpenChange={setShowCelebration}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <div className="flex items-center gap-2 mb-2">
            <img src="/theuniversaltree-logo.jpeg" alt="" className="w-8 h-8 rounded-full" />
            <span className="font-semibold text-[#1A1D1A]">Universal Tree</span>
          </div>
          <div className="text-center py-2">
            <div className="w-16 h-16 rounded-full bg-[#22C55E]/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={40} className="text-[#22C55E]" />
            </div>
            <h2 className="text-2xl font-semibold text-[#1A1D1A] mb-2">Congratulations!</h2>
            <p className="text-[#646964]">Your application has been approved.</p>
            <p className="text-sm text-[#646964] mt-4 leading-relaxed">
              Thank you for joining Universal Tree. Start accepting bookings and grow your business.
            </p>
            <Button onClick={() => setShowCelebration(false)} className="mt-6 w-full bg-[#1C4532] hover:bg-[#143324] h-12 text-sm font-semibold rounded-xl">
              Continue your jobs
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <BookingDetailsDialog bookingId={chatBookingId} open={chatOpen} onOpenChange={setChatOpen}/>
    </div>
    </>
  );
}
